import React, { useState } from 'react';
import styles from './expandingCards.module.scss';
import LargeWidthContainer from '../Layout/Containers/LargeWidthContainer';

export interface CardItem {
    title: string;
    description: string;
    tag?: string;
    points?: string[];
}

const defaultItems: CardItem[] = [
    {
        tag: '01',
        title: 'Changing critical systems',
        description:
            'When systems change, integrations, data and quality need to work together. We contribute expertise in architecture, development and testing to help you plan and carry out the work.',
    },
    {
        tag: '02',
        title: 'Adding senior expertise to your team',
        description:
            'When you need experienced people alongside your own, we strengthen your team with consultants who understand business needs, contribute to decisions and take responsibility for agreed areas of work.',
    },
    {
        tag: '03',
        title: 'Moving AI and automation forward',
        description:
            'When you want to improve a process or move an AI initiative forward, we help assess opportunities, data, technology and ways of working, and identify practical next steps.',
    },
];

interface ExpandingCardsProps {
    id?: string;
    eyebrow?: string;
    heading?: string;
    items?: CardItem[];
    // Number of columns on wide screens: 3 (default) or 4
    columns?: 3 | 4;
}

const ExpandingCardsComponent = ({
    id,
    eyebrow = 'How we can help',
    heading = 'Where do you need to strengthen your IT organisation?',
    items = defaultItems,
    columns = 3,
}: ExpandingCardsProps) => {
    // The first card is active by default; hovering or focusing another card takes over
    const [activeIndex, setActiveIndex] = useState(0);

    return (
        <LargeWidthContainer extraClassNames={styles.expandingCards}>
            <div className="container" id={id} onMouseLeave={() => setActiveIndex(0)}>
                <span className={styles.eyebrow}>{eyebrow}</span>
                <h2 className={styles.heading}>{heading}</h2>
                <div
                    className={`${styles.cards} ${columns === 4 ? styles.cardsFour : styles.cardsThree}`}
                >
                    {items.map((item, index) => (
                        <div
                            key={item.title}
                            className={`${styles.card} ${
                                index === activeIndex ? styles.cardActive : ''
                            }`}
                            tabIndex={0}
                            onMouseEnter={() => setActiveIndex(index)}
                            onFocus={() => setActiveIndex(index)}
                        >
                            <span className={styles.cardNumber}>{item.tag}</span>
                            <div className={styles.cardBody}>
                                <h3 className={styles.cardTitle}>{item.title}</h3>
                                <div className={styles.cardDescriptionWrap}>
                                    <div className={styles.cardDescription}>
                                        <p>{item.description}</p>
                                        {item.points && (
                                            <ul className={styles.cardPoints}>
                                                {item.points.map((point) => (
                                                    <li key={point}>{point}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </LargeWidthContainer>
    );
};

export default ExpandingCardsComponent;
